import { useEffect, useState } from 'react';
import type { MailClient } from '../api/client';
import type { Mailbox } from '../api/types';
import { useT } from '../i18n';
import { useOverlay } from '../ui/overlays';
import { useToasts } from '../ui/toasts';

/*
    Emptying the Trash or the Junk folder, asked once.

    Reached from two places — the folder's menu and the strip over its list — because those are
    the two places people look for it, and one dialog so the two cannot say different things.
    Until 2026-10-12 neither existed: the folder menu's only destructive entry was "Sil…", which
    deletes the folder, and that is what someone clearing out the Trash nearly did.

    No name to type, unlike deleting a folder that holds mail: the folder stays, and what goes is
    already in the Trash or Junk. The count is said out loud instead, so nobody is surprised by it.
*/
export default function EmptyFolderDialog({
    client,
    mailbox,
    label,
    onClose,
    onEmptied,
}: {
    client: MailClient;
    mailbox: Mailbox;
    /** The folder's name as the tree shows it — "Çöp Kutusu", not "Trash". */
    label: string;
    onClose: () => void;
    onEmptied: () => void;
}) {
    const { t, num } = useT();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useOverlay();

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !busy) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    const empty = async () => {
        setBusy(true);
        setError(null);
        try {
            const count = await client.emptyMailbox(mailbox.id);
            useToasts.getState().show({ message: 'folders.emptied', values: { name: label, count: num(count) } });
            onEmptied();
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            setBusy(false);
        }
    };

    return (
        <>
            <div onClick={() => (busy ? undefined : onClose())} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.45)' }} />
            <div
                role="alertdialog"
                aria-label={t('folders.emptying', { name: label })}
                data-testid="empty-folder-dialog"
                style={{
                    position: 'fixed',
                    left: '50%',
                    top: '32%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 71,
                    width: 'min(400px, calc(100vw - 40px))',
                    padding: 18,
                    background: 'var(--surface)',
                    border: '1px solid var(--border2)',
                    borderRadius: 'var(--r-card)',
                    boxShadow: '0 22px 60px rgba(0,0,0,0.5)',
                    display: 'grid',
                    gap: 12,
                }}
            >
                <div>
                    <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>{t('folders.emptying', { name: label })}</div>
                    <div style={{ marginTop: 6, fontSize: 'var(--fs-sm)', color: 'var(--text-dim)', lineHeight: 1.5 }}>
                        {t('folders.emptyBody', { name: label, count: num(mailbox.totalEmails) })}
                    </div>
                </div>
                {error ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{error}</div> : null}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        disabled={busy}
                        autoFocus
                        style={{
                            height: 30,
                            padding: '0 12px',
                            background: 'transparent',
                            border: '1px solid var(--border2)',
                            borderRadius: 'var(--r-btn)',
                            color: 'var(--text-dim)',
                            fontSize: 'var(--fs-sm)',
                        }}
                    >
                        {t('app.cancel')}
                    </button>
                    <button
                        data-testid="empty-folder-confirm"
                        onClick={() => void empty()}
                        disabled={busy}
                        style={{
                            height: 30,
                            padding: '0 14px',
                            background: 'var(--red)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 'var(--r-btn)',
                            fontSize: 'var(--fs-sm)',
                            fontWeight: 600,
                        }}
                    >
                        {t('folders.emptyConfirm')}
                    </button>
                </div>
            </div>
        </>
    );
}
